import { useEffect, useMemo, useRef, useState } from 'react';
import { addDays, format, isBefore, parseISO, startOfDay } from 'date-fns';
import { useFlags } from 'launchdarkly-react-client-sdk';

import Button from 'components/button/Button';
import Modal from 'components/modal';
import {
  HeaderContainer,
  ModalActions,
  ModalContainer,
  ModalContent,
} from 'components/modal/styles';
import Notification from 'components/notification';

import { deleteDeadline, postDeadline } from 'services/playlists';

import DeadlineSelector, { DEFAULT_TRAINING_PACE, DeadlineSelectorValue } from './DeadlineSelector';
import {
  CompletedUsersNotice,
  DrawerBody,
  FooterActions,
  ManageDeadlineModalGlobalStyles,
  PlaylistDurationRefreshingText,
  PlaylistDurationText,
  SectionTitle,
} from './styles';

export interface ManageDeadlineDrawerProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  playlistId: string;
  businessAccountId: string;
  playlistDurationSeconds?: number;
  userId?: string;
  currentDeadline?: {
    due_date: string | null;
    deadline_type: 'custom' | 'recommended' | null;
    training_pace?: number | null;
  } | null;
  userProgress?: number;
  userIds?: string[];
  hasCompletedUsersInBulkSelection?: boolean;
  isPlaylistDurationRefreshing?: boolean;
}

const getInitialValue = (
  props: Pick<ManageDeadlineDrawerProps, 'userId' | 'currentDeadline' | 'userIds'>
): DeadlineSelectorValue => {
  const isBulk = Array.isArray(props.userIds) && !props.userId;

  if (isBulk) {
    return { type: 'none', date: null };
  }

  if (props.currentDeadline?.deadline_type === 'recommended') {
    return {
      type: 'recommended',
      date: props.currentDeadline.due_date ?? null,
      trainingPaceHoursPerWeek: props.currentDeadline.training_pace ?? DEFAULT_TRAINING_PACE,
    };
  }

  if (props.currentDeadline?.due_date) {
    return { type: 'custom', date: props.currentDeadline.due_date };
  }

  return { type: 'none', date: null };
};

const valuesAreEqual = (a: DeadlineSelectorValue, b: DeadlineSelectorValue): boolean =>
  a.type === b.type &&
  a.date === b.date &&
  a.trainingPaceHoursPerWeek === b.trainingPaceHoursPerWeek;

const formatPlaylistDuration = (seconds: number): string => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);

  return `${h}h ${m}m`;
};

const getErrorDescription = (error: unknown): string | undefined => {
  const typedError = error as {
    message?: string;
    response?: {
      data?: { detail?: string; details?: string; failed?: Array<{ reason?: string }> } | string;
    };
  };

  const responseData = typedError.response?.data;

  if (typeof responseData === 'string') {
    return responseData;
  }

  if (responseData?.details) {
    return responseData.details;
  }

  if (responseData?.detail) {
    return responseData.detail;
  }

  const failedReason = responseData?.failed?.find((item) => item.reason)?.reason;

  if (failedReason) {
    return failedReason;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return undefined;
};

const hasPastCustomDate = (value: DeadlineSelectorValue): boolean =>
  value.type === 'custom' && Boolean(value.date)
    ? isBefore(startOfDay(parseISO(value.date as string)), startOfDay(new Date()))
    : false;

const getDeadlinePayload = (
  value: DeadlineSelectorValue
): {
  due_date: string;
  deadline_type: 'custom' | 'recommended';
  training_pace?: number;
} | null => {
  if ((value.type === 'custom' || value.type === 'recommended') && value.date) {
    return {
      due_date: value.date,
      deadline_type: value.type,
      ...(value.type === 'recommended'
        ? { training_pace: value.trainingPaceHoursPerWeek ?? DEFAULT_TRAINING_PACE }
        : {}),
    };
  }

  return null;
};

const ManageDeadlineDrawer = ({
  visible,
  onClose,
  onSuccess,
  playlistId,
  businessAccountId,
  playlistDurationSeconds,
  userId,
  currentDeadline,
  userProgress = 0,
  userIds,
  hasCompletedUsersInBulkSelection = false,
  isPlaylistDurationRefreshing = false,
}: ManageDeadlineDrawerProps): JSX.Element | null => {
  const { playlistDeadlinesMvp } = useFlags();

  const isBulk = Array.isArray(userIds) && !userId;
  const resolvedUserIds: string[] = isBulk ? userIds : userId ? [userId] : [];

  const initialValue = useMemo(
    () => getInitialValue({ userId, currentDeadline, userIds }),
    [userId, currentDeadline, userIds]
  );

  const [value, setValue] = useState<DeadlineSelectorValue>(initialValue);
  const [loading, setLoading] = useState(false);
  const isMountedRef = useRef(true);

  // A recommended deadline recalculates from today, so re-saving it is always allowed
  // even when the stored values are unchanged.
  const hasChanged = isBulk || value.type === 'recommended' || !valuesAreEqual(value, initialValue);
  const requiresDate = (value.type === 'custom' || value.type === 'recommended') && !value.date;
  const hasInvalidCustomDate = hasPastCustomDate(value);
  const isRecommendedBlockedWhileRefreshing =
    isPlaylistDurationRefreshing && value.type === 'recommended';
  const selectorUserProgress = isBulk && resolvedUserIds.length !== 1 ? 0 : userProgress;

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue, visible]);

  useEffect(() => {
    if (value.type !== 'recommended') return;

    const pace = value.trainingPaceHoursPerWeek ?? DEFAULT_TRAINING_PACE;
    const remainingSeconds = Math.max(
      0,
      (playlistDurationSeconds ?? 0) * (1 - selectorUserProgress)
    );
    const remainingHours = remainingSeconds / 3600;
    const daysNeeded = Math.max(1, Math.ceil((remainingHours / pace) * 7));
    const recommendedDate = format(addDays(new Date(), daysNeeded), 'yyyy-MM-dd');

    if (value.date === recommendedDate && value.trainingPaceHoursPerWeek != null) return;

    setValue((prev) => ({
      ...prev,
      date: recommendedDate,
      trainingPaceHoursPerWeek: prev.trainingPaceHoursPerWeek ?? DEFAULT_TRAINING_PACE,
    }));
  }, [
    playlistDurationSeconds,
    selectorUserProgress,
    value.date,
    value.trainingPaceHoursPerWeek,
    value.type,
  ]);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  if (!playlistDeadlinesMvp) return null;

  const handleSave = async () => {
    if (
      !hasChanged ||
      requiresDate ||
      hasInvalidCustomDate ||
      isRecommendedBlockedWhileRefreshing
    ) {
      return;
    }

    setLoading(true);

    try {
      if (value.type === 'none') {
        await deleteDeadline({
          playlist_id: playlistId,
          user_ids: resolvedUserIds,
          business_account_id: businessAccountId,
        });
      } else {
        const payload = getDeadlinePayload(value);

        if (!payload) {
          return;
        }

        await postDeadline({
          playlist_id: playlistId,
          user_ids: resolvedUserIds,
          due_date: payload.due_date,
          deadline_type: payload.deadline_type,
          training_pace: payload.training_pace,
          business_account_id: businessAccountId,
        });
      }

      onClose();
      onSuccess();
      Notification({ text: 'Deadline updated', type: 'success', singleLine: true });
    } catch (error: unknown) {
      Notification({
        text: 'Failed to update deadline',
        description: getErrorDescription(error),
        type: 'error',
      });
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  return (
    <>
      <ManageDeadlineModalGlobalStyles />
      <Modal
        visible={visible}
        onCancel={onClose}
        footer={null}
        width={595}
        closeIconColor="#fff"
        wrapClassName="manage-deadline-modal"
        destroyOnClose
      >
        <ModalContainer>
          <HeaderContainer>
            <h2>Manage user deadlines</h2>
          </HeaderContainer>

          <ModalContent>
            <DrawerBody>
              <div>
                {isBulk && hasCompletedUsersInBulkSelection && (
                  <CompletedUsersNotice>
                    Some selected users have already completed this playlist, so their deadlines
                    won’t be updated
                  </CompletedUsersNotice>
                )}
                <SectionTitle>Deadline settings</SectionTitle>
                {playlistDurationSeconds != null && playlistDurationSeconds > 0 && (
                  <PlaylistDurationText>
                    Playlist duration:{' '}
                    <span>{formatPlaylistDuration(playlistDurationSeconds)}</span>
                  </PlaylistDurationText>
                )}
                {isPlaylistDurationRefreshing && (
                  <PlaylistDurationRefreshingText>
                    Updating playlist duration...
                  </PlaylistDurationRefreshingText>
                )}
              </div>

              <DeadlineSelector
                value={value}
                onChange={setValue}
                playlistDurationSeconds={playlistDurationSeconds}
                userProgress={selectorUserProgress}
                disableRecommended={isPlaylistDurationRefreshing}
                isBulk={isBulk}
                selectedUsersCount={resolvedUserIds.length}
              />
            </DrawerBody>
          </ModalContent>

          <ModalActions>
            <FooterActions>
              <Button
                aria-label="Update deadline"
                $primary
                loading={loading}
                disabled={
                  !hasChanged ||
                  requiresDate ||
                  hasInvalidCustomDate ||
                  isRecommendedBlockedWhileRefreshing
                }
                onClick={handleSave}
              >
                Save deadline
              </Button>
            </FooterActions>
          </ModalActions>
        </ModalContainer>
      </Modal>
    </>
  );
};

export default ManageDeadlineDrawer;
